import type { JSONContent } from '@tiptap/core';

export interface HeadingOutlineItem {
  id: string;
  level: number;
  text: string;
}

function slugify(text: string): string {
  const base = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return base || 'secao';
}

function textOf(node: JSONContent): string {
  if (node.text) return node.text;
  if (!node.content) return '';
  return node.content.map(textOf).join('');
}

/**
 * Sumário (headings H1/H2 do corpo do artigo) com id estável e único por
 * item — usado pelo menu lateral (ArticleOutline) e para estampar o mesmo id
 * no HTML renderizado (ver withHeadingIds), assim o link do sumário aponta
 * pro heading certo.
 */
export function extractHeadingOutline(doc: JSONContent): HeadingOutlineItem[] {
  const seen = new Map<string, number>();
  const items: HeadingOutlineItem[] = [];

  function visit(node: JSONContent) {
    if (node.type === 'heading') {
      const text = textOf(node).trim();
      if (text) {
        const base = slugify(text);
        const count = seen.get(base) ?? 0;
        seen.set(base, count + 1);
        const id = count === 0 ? base : `${base}-${count + 1}`;
        items.push({ id, level: Number(node.attrs?.level ?? 1), text });
      }
    }
    node.content?.forEach(visit);
  }

  doc.content?.forEach(visit);
  return items;
}

/**
 * Clona o doc e injeta em cada heading o mesmo id que extractHeadingOutline
 * geraria (mesma ordem de percurso) — o HTML gerado por generateHTML só
 * inclui atributos declarados no schema (ver HeadingWithAnchor em
 * blockEditorExtensions.ts), então sem isso o `id` nunca apareceria no `<h1>`/
 * `<h2>` renderizado e o sumário não teria pra onde rolar.
 */
export function withHeadingIds(doc: JSONContent): JSONContent {
  const outline = extractHeadingOutline(doc);
  let i = 0;

  function visit(node: JSONContent): JSONContent {
    if (node.type === 'heading') {
      const item = outline[i];
      i += 1;
      return { ...node, attrs: { ...node.attrs, id: item?.id ?? null }, content: node.content?.map(visit) };
    }
    if (node.content) {
      return { ...node, content: node.content.map(visit) };
    }
    return node;
  }

  return { ...doc, content: doc.content?.map(visit) };
}
